import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import { StatBar, StorageModeFilter, useStorageModeFilter } from '~/components/common';
import { IFunnelResponse, IFunnelStep } from '~/utils/interface/analytics';

const FunnelColumn = ({ title, steps }: { title: string; steps: IFunnelStep[] }) => {
  const { t } = useTranslation();
  const total = steps[0]?.count || 1;

  return (
    <div className='flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-6'>
      <h2 className='text-base font-semibold text-foreground'>{title}</h2>
      <div className='flex flex-col gap-4'>
        {steps.map((step) => (
          <div key={step.eventName} className='flex flex-col gap-1'>
            <StatBar
              label={
                t(`pages.funnel.steps.${step.eventName}`, {
                  defaultValue: step.eventName,
                })
              }
              value={step.count}
              total={total}
            />
            {step.failedCount > 0 && (
              <p className='text-xs text-destructive'>
                {t('pages.funnel.failedAttempts', { count: step.failedCount })}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Funnel = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<IFunnelResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const storageMode = useStorageModeFilter();
  const [households, setHouseholds] = useState<Record<string, number>>({});

  useEffect(() => {
    analyticsApi
      .getStorageModes()
      .then((res) =>
        setHouseholds(Object.fromEntries(res.data.data.map((row) => [row.storageMode, row.households])))
      )
      .catch(() => setHouseholds({}));
  }, []);

  useEffect(() => {
    analyticsApi
      .getFunnel(storageMode)
      .then((res) => setData(res.data.data))
      .catch(() => setError(t('pages.funnel.loadError')))
      .finally(() => setIsLoading(false));
  }, [t, storageMode]);

  return (
    <div>
      <h1 className='text-xl font-semibold text-foreground'>{t('pages.funnel.title')}</h1>
      <p className='mt-1 text-sm text-muted-foreground'>{t('pages.funnel.description')}</p>
      <p className='mt-2 text-sm text-foreground'>
        {t('pages.funnel.households', { server: households.SERVER ?? 0, local: households.LOCAL ?? 0 })}
      </p>
      <StorageModeFilter />

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{error}</p>}

      {data && (
        <div className='mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <FunnelColumn title={t('pages.funnel.seniorApp')} steps={data.senior} />
          <FunnelColumn title={t('pages.funnel.caregiverApp')} steps={data.caregiver} />
        </div>
      )}
    </div>
  );
};

export default Funnel;
